/**
 * TypeScript mirror of the WP4 rooms contract (`docs/specs/WP4-rooms.md`): room lifecycle,
 * membership, invites, field values, locks, sections, and reveals. Query objects follow the same
 * shape as `templatesQuery`/`meQuery` (plain `{queryKey, queryFn}`, no hooks); mutations are plain
 * functions taking a single variables object so they drop straight into `useMutation({mutationFn})`
 * at call sites — never call `fetch` directly from a component.
 */
import { apiGet, apiSend } from './api';
import type { FieldValue, RowKind } from './fieldValue';
import type { Field, Template } from './templates';

export type RoomStatus = 'DRAFT' | 'LIVE' | 'FINISHED' | 'ARCHIVED';
export type RoomRole = 'HOST' | 'COHOST' | 'GUEST' | 'VIEWER';
export type LockTargetKind = 'FIELD' | 'SECTION';

export interface UserRef {
  id: number;
  username: string;
}

export interface RoomSummary {
  id: number;
  title: string;
  guestLabel: string;
  className: string;
  classSlug: string;
  status: RoomStatus;
  templateKey: string;
  seriesLabel: string;
  myRole: RoomRole;
  updatedAt: string;
}

export interface Reveal {
  id: number;
  fieldId: number;
  rowKind: RowKind;
  value: FieldValue;
  revealedAt: string;
}

export interface Room {
  id: number;
  title: string;
  guestLabel: string;
  guestUserId: number | null;
  videoUrl: string | null;
  status: RoomStatus;
  templateId: number;
  classId: number;
  className: string;
  classSlug: string;
  currentSectionId: number | null;
  streamStartedAt: string | null;
  chapterOffsetSeconds: number;
  activeReveal: Reveal | null;
  createdBy: UserRef;
  createdAt: string;
  roomVersion: number;
}

export interface Member {
  userId: number;
  username: string;
  avatarUrl: string | null;
  role: RoomRole;
  joinedAt: string;
}

/** One row of `field_value`, as embedded in a snapshot or returned by the PUT endpoint. */
export interface FieldCell {
  fieldId: number;
  rowKind: RowKind;
  value: FieldValue | null;
  version: number;
  updatedBy: UserRef | null;
  updatedAt: string | null;
}

export interface LockEntry {
  targetKind: LockTargetKind;
  targetId: number;
  /** Optional/nullable: the REST snapshot always carries who holds the lock, but the WP5/WP6
   * `locks` STOMP topic is people-free by contract (see `docs/specs/WP5-realtime.md`) — a lock
   * applied from that broadcast simply omits this field. */
  lockedBy?: UserRef | null;
  lockedAt: string;
}

export interface LockResult {
  targetKind: LockTargetKind;
  targetId: number;
  locked: boolean;
  lockedBy: UserRef | null;
}

export interface SectionTimestampEntry {
  sectionId: number;
  enteredAt: string;
  enteredBy: UserRef;
}

export interface RoomSnapshot {
  room: Room;
  template: Template;
  members: Member[];
  fields: FieldCell[];
  locks: LockEntry[];
  sectionTimestamps: SectionTimestampEntry[];
  myRole: RoomRole;
}

/** A role an invite can grant — never HOST (the room creator is the only way to become one). */
export type InviteRole = Exclude<RoomRole, 'HOST'>;

export interface Invite {
  id: number;
  token: string;
  role: InviteRole;
  expiresAt: string;
  maxUses: number | null;
  useCount: number;
  revokedAt: string | null;
  url: string;
}

export interface InvitePreview {
  roomTitle: string;
  className: string;
  guestLabel: string;
  role: RoomRole;
  hostUsername: string;
}

export interface OverlayToken {
  token: string;
  url: string;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const roomsQuery = {
  queryKey: ['rooms'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<RoomSummary[]>('/api/rooms', signal),
};

export function roomQuery(id: number) {
  return {
    queryKey: ['rooms', id] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<RoomSnapshot>(`/api/rooms/${id}`, signal),
  };
}

export interface AdminRoomFilters {
  status?: RoomStatus;
  templateId?: number;
}

/** `GET /api/admin/rooms` (WP8: gains optional `status`/`templateId` filters) — every room in the
 * system, not just the caller's own; `AdminRooms` is the only consumer. */
export function adminRoomsQuery(filters: AdminRoomFilters) {
  const params = new URLSearchParams();
  if (filters.status !== undefined) {
    params.set('status', filters.status);
  }
  if (filters.templateId !== undefined) {
    params.set('templateId', String(filters.templateId));
  }
  const query = params.toString();
  const path = query === '' ? '/api/admin/rooms' : `/api/admin/rooms?${query}`;
  return {
    queryKey: ['admin', 'rooms', filters.status ?? null, filters.templateId ?? null] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<RoomSummary[]>(path, signal),
  };
}

/** 404 (unknown/expired/revoked/exhausted) is a legitimate answer here, not worth retrying. */
export function invitePreviewQuery(token: string) {
  return {
    queryKey: ['invites', token] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<InvitePreview>(`/api/invites/${token}`, signal),
    retry: false,
  };
}

export function invitesQuery(roomId: number) {
  return {
    queryKey: ['rooms', roomId, 'invites'] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<Invite[]>(`/api/rooms/${roomId}/invites`, signal),
  };
}

export function overlayTokenQuery(roomId: number) {
  return {
    queryKey: ['rooms', roomId, 'overlay-token'] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<OverlayToken>(`/api/rooms/${roomId}/overlay-token`, signal),
  };
}

// ---------------------------------------------------------------------------
// Mutations — each takes one variables object so it drops straight into
// `useMutation({ mutationFn })` at the call site.
// ---------------------------------------------------------------------------

export interface CreateRoomInput {
  templateId: number;
  classId: number;
  guestLabel: string;
  title?: string;
}

export function createRoom(input: CreateRoomInput): Promise<RoomSnapshot> {
  return apiSend('POST', '/api/rooms', input);
}

export interface PatchRoomInput {
  roomId: number;
  title?: string;
  guestLabel?: string;
  videoUrl?: string;
  classId?: number;
  chapterOffsetSeconds?: number;
}

export function patchRoom({ roomId, ...body }: PatchRoomInput): Promise<Room> {
  return apiSend('PATCH', `/api/rooms/${roomId}`, body);
}

export function setStatus(vars: { roomId: number; status: RoomStatus }): Promise<Room> {
  return apiSend('POST', `/api/rooms/${vars.roomId}/status`, { status: vars.status });
}

export function setStreamStart(vars: { roomId: number; at?: string }): Promise<Room> {
  return apiSend('POST', `/api/rooms/${vars.roomId}/stream-start`, vars.at ? { at: vars.at } : {});
}

export function putField(vars: {
  roomId: number;
  fieldId: number;
  rowKind: RowKind;
  value: FieldValue | null;
}): Promise<FieldCell> {
  return apiSend('PUT', `/api/rooms/${vars.roomId}/fields/${vars.fieldId}/${vars.rowKind}`, {
    value: vars.value,
  });
}

export function setLock(vars: { roomId: number; targetKind: LockTargetKind; targetId: number }): Promise<LockResult> {
  return apiSend('PUT', `/api/rooms/${vars.roomId}/locks/${vars.targetKind}/${vars.targetId}`);
}

export function clearLock(vars: {
  roomId: number;
  targetKind: LockTargetKind;
  targetId: number;
}): Promise<LockResult> {
  return apiSend('DELETE', `/api/rooms/${vars.roomId}/locks/${vars.targetKind}/${vars.targetId}`);
}

export function setSection(vars: { roomId: number; sectionId: number | null }): Promise<Room> {
  return apiSend('POST', `/api/rooms/${vars.roomId}/section`, { sectionId: vars.sectionId });
}

export function reveal(vars: { roomId: number; fieldId: number; rowKind: RowKind }): Promise<{ reveal: Reveal }> {
  return apiSend('POST', `/api/rooms/${vars.roomId}/reveal`, { fieldId: vars.fieldId, rowKind: vars.rowKind });
}

export function hideReveal(vars: { roomId: number }): Promise<void> {
  return apiSend('DELETE', `/api/rooms/${vars.roomId}/reveal`);
}

export interface CreateInviteInput {
  roomId: number;
  role: InviteRole;
  expiresInMinutes: number;
  maxUses?: number;
}

export function createInvite({ roomId, ...body }: CreateInviteInput): Promise<Invite> {
  return apiSend('POST', `/api/rooms/${roomId}/invites`, body);
}

export function revokeInvite(vars: { roomId: number; inviteId: number }): Promise<void> {
  return apiSend('DELETE', `/api/rooms/${vars.roomId}/invites/${vars.inviteId}`);
}

export function acceptInvite(vars: { token: string }): Promise<{ roomId: number; role: RoomRole }> {
  return apiSend('POST', `/api/invites/${vars.token}/accept`);
}

export function setMemberRole(vars: { roomId: number; userId: number; role: RoomRole }): Promise<Member> {
  return apiSend('PATCH', `/api/rooms/${vars.roomId}/members/${vars.userId}`, { role: vars.role });
}

export function removeMember(vars: { roomId: number; userId: number }): Promise<void> {
  return apiSend('DELETE', `/api/rooms/${vars.roomId}/members/${vars.userId}`);
}

export function rotateOverlayToken(vars: { roomId: number }): Promise<OverlayToken> {
  return apiSend('POST', `/api/rooms/${vars.roomId}/overlay-token/rotate`);
}

// ---------------------------------------------------------------------------
// Pure helpers shared by the store and the room UI
// ---------------------------------------------------------------------------

export function cellKey(fieldId: number, rowKind: RowKind): string {
  return `${fieldId}:${rowKind}`;
}

/** Which `field_value` rows exist for a field, mirroring the backend's `appliesTo` switch. */
export function allowedRowKinds(field: Pick<Field, 'appliesTo'>): RowKind[] {
  switch (field.appliesTo) {
    case 'BOTH_ROWS':
      return ['PLAYER', 'COMMUNITY'];
    case 'PLAYER_ONLY':
      return ['PLAYER'];
    case 'COMMUNITY_ONLY':
      return ['COMMUNITY'];
    case 'ROOM':
      return ['ROOM'];
  }
}

/**
 * Mirrors `RoomAccessService`'s EDIT_FIELD rule for optimistic UI (the server is still the
 * authority — a stale lock/status here just means a mutation bounces with 403/409): VIEWER never
 * edits; a GUEST may not write COMMUNITY; a FINISHED/ARCHIVED room only accepts HOST writes; a
 * locked field or its section blocks everyone except HOST/COHOST.
 */
export function canEdit(
  role: RoomRole,
  field: Pick<Field, 'id' | 'sectionId'>,
  rowKind: RowKind,
  locks: LockEntry[],
  status: RoomStatus,
): boolean {
  if (role === 'VIEWER') {
    return false;
  }
  if ((status === 'FINISHED' || status === 'ARCHIVED') && role !== 'HOST') {
    return false;
  }
  if (role === 'GUEST' && rowKind === 'COMMUNITY') {
    return false;
  }
  const isHostOrCohost = role === 'HOST' || role === 'COHOST';
  if (isHostOrCohost) {
    return true;
  }
  const fieldLocked = locks.some((lock) => lock.targetKind === 'FIELD' && lock.targetId === field.id);
  const sectionLocked = locks.some((lock) => lock.targetKind === 'SECTION' && lock.targetId === field.sectionId);
  return !fieldLocked && !sectionLocked;
}
